<template>
  <div>
    <h1 class="d-flex align-items-center">
      <span class="icon mr-2 icon-produkte_statistik" aria-hidden="true"></span>
      {{ $t('views.occupancyReport.title') }}
    </h1>

    <link-back :to="'/reports'" i18nMessage="common.backToOverview" />

    <div class="show-in-print">
      <h3>{{roomName(roomId)}}</h3>
      <p><strong>{{cleanType(timeSlotType)}}:</strong> {{formatDay(dateFrom)}} - {{formatDay(dateUntil)}}</p>
    </div>

      <form @submit.prevent="checkAndLoadFromServer()" class="mt-2 d-print-none">

        <div class="row">
          <div class="col-12 col-md-6">
            <control-date
              i18nLabel="views.occupancyReport.dateFrom"
              :validation="v$.dateFrom"
              v-model="dateFrom"
              @update:modelValue="checkAndLoadFromServer()"
            />
          </div>
          <div class="col-12 col-md-6">
            <control-date
              i18nLabel="views.occupancyReport.dateUntil"
              :validation="v$.dateUntil"
              v-model="dateUntil"
              @update:modelValue="checkAndLoadFromServer()"
            />
          </div>
          <div class="col-12 col-md-8">
            <control-select
              i18nLabel="views.occupancyReport.room"
              :validation="v$.roomId"
              v-model="roomId"
              :availableValues="availableRooms()"
              @update:modelValue="checkAndLoadFromServer()"
            />
          </div>
          <div class="col-12 col-md-4">
            <control-select
              i18nLabel="views.occupancyReport.timeSlotType"
              :validation="v$.timeSlotType"
              v-model="timeSlotType"
              :availableValues="availableTimeSlotTypes()"
              @update:modelValue="checkAndLoadFromServer()"
            />
          </div>
        </div>
      </form>
    <loading-container :state="state" errorLinkTo="/reports">

      <div class="d-flex justify-content-end"><button-print /></div>
      <result-list-occupancy :reportType="timeSlotType" :reportItems="ticketReportItems" v-if="ticketReportItems.length > 0" />
      <feedback-message v-else :message="$t('views.occupancyReport.noEntry')" />
    </loading-container>
  </div>
</template>

<script lang="ts">
import ControlDate from '@/components/controls/ControlDate.vue';
import ControlSelect from '@/components/controls/ControlSelect.vue';
import FeedbackMessage from '@/components/feedback/FeedbackMessage.vue';
import ResultListOccupancy from '@/components/filterforms/ResultListOccupancy.vue';
import LinkBack from '@/components/interaction/LinkBack.vue';
import LoadingContainer from '@/components/structure/LoadingContainer.vue';
import { Room, TicketReportItem } from '@/generated/openapi';
import LoadingMixin from '@/mixins/LoadingMixin';
import { SelectOption } from '@/models/SelectOption';
import { adminService } from '@/services/adminService';
import { reportService } from '@/services/reportService';
import { now } from '@/utils/dateTimeUtils';
import { validateAndFocusInvalid } from '@/utils/formUtils';
import { formatDateString } from '@/utils/formatUtils';
import { LoadingState } from '@/utils/loadingUtils';
import { isDateString } from '@/utils/validationUtils';
import useVuelidate from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { defineComponent } from 'vue';
import ButtonPrint from '@/components/interaction/ButtonPrint.vue';

export default defineComponent({
  name: 'OccupancyReport',
  mixins: [
    LoadingMixin
  ],
  data() {
    return {
      ticketReportItems: [] as TicketReportItem[],
      dateFrom: now().minus({
        months: 1
      }).toISODate(),
      dateUntil: now().toISODate(),
      rooms: [] as Room[],
      roomId: '',
      timeSlotType: "BOOKED" as "BOOKED"|"CANCELLED"
    }
  },
  setup() {
    return {
      v$: useVuelidate()
    }
  },
  validations() {
    return {
      dateFrom: {
        required,
        isDateString,
      },
      dateUntil: {
        required,
        isDateString,
      },
      roomId: {
        required
      },
      timeSlotType: {
        required
      }
    }
  },
  created() {
    adminService.getAllRooms()
      .then(rooms => {
        if (rooms.length < 1) {
          this.state = LoadingState.error
          return;
        }
        this.rooms = rooms
        this.roomId = rooms[0].id
        this.checkAndLoadFromServer()
      }).catch(this.handleErrorWithI18nString)
  },
  methods: {
    checkAndLoadFromServer() {
      validateAndFocusInvalid(this.v$, this.$el).then(() => {
        this.state = LoadingState.loading
        reportService.getOccupancyByRoomAndDate(this.roomId, this.dateFrom, this.dateUntil, this.timeSlotType)
          .then(resp => {
            this.ticketReportItems = resp
            this.state = LoadingState.success
          }).catch(this.handleErrorWithI18nString)
      })
    },
    availableTimeSlotTypes(): SelectOption[] {
      return ["BOOKED", "CANCELLED"].map(value => ({
        value,
        label: this.$t('views.occupancyReport.timeSlotTypes.' + value)
      }))
    },
    availableRooms(): SelectOption[] {
      return this.rooms.map(room => ({
        value: room.id,
        label: room.name
      }))
    },
    formatDay(day: string){
      return formatDateString(day)
    },
    cleanType(type: string){
      return this.$t('views.occupancyReport.timeSlotTypes.' + type)
    },
    roomName(type: string){
      for(let i = 0; i < this.rooms.length; i++){
        if(this.rooms[i].id === type){
          return this.rooms[i].name;
        }
      }
      return '';
    }
  },
  components: {
    LoadingContainer,
    ControlDate,
    LinkBack,
    ControlSelect,
    ResultListOccupancy,
    FeedbackMessage,
    ButtonPrint
  }
});
</script>
